import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Calendar as CalendarType } from '../../types/calendar';
import { Twitch, Youtube, Calendar, Users, Globe2, Bell, Lock } from 'lucide-react';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import NextEventCountdown from './NextEventCountdown';
import SubscriptionPrompt from '../SubscriptionPrompt';
import EventFeedbackSection from './EventFeedbackSection';
import PastEventsSection from './PastEventsSection';
import { getGoogleCalendarSubscribeUrl } from '../../utils/calendarUrl';
import { createSubscription, createPayment } from '../services/stripeService';
import { toast } from '../../utils/toast';
import SubscribeButton from '../SubscribeButton';
import PurchaseTicketButton from '../PurchaseTicketButton';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useTicketStatus } from '../../hooks/useTicketStatus';
import SubscriptionStatus from '../SubscriptionStatus';
import { createFreeSubscription } from '../../services/subscriptionService';

interface CalendarLinksProps {
  calendar: CalendarType;
  isSubscribed: boolean;
  onSubscriptionChange: () => void;
  subscriberCount: number;
}

export default function CalendarLinks({ calendar, isSubscribed, onSubscriptionChange, subscriberCount }: CalendarLinksProps) {
  const { user } = useAuth();
  const { events } = useGoogleCalendar(calendar.google_calendar_url);
  const [subscribing, setSubscribing] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [alertLoading, setAlertLoading] = useState(false);

  const loadAlertPreference = useCallback(async () => {
    if (!user || !isSubscribed) return;
    const { data } = await supabase
      .from('subscriptions')
      .select('alert_enabled')
      .eq('user_id', user.id)
      .eq('calendar_id', calendar.id)
      .maybeSingle();
    if (data) setAlertEnabled(data.alert_enabled ?? true);
  }, [user, isSubscribed, calendar.id]);

  useEffect(() => {
    loadAlertPreference();
  }, [loadAlertPreference]);

  const handleToggleAlert = async () => {
    if (!user || alertLoading) return;
    setAlertLoading(true);
    const newValue = !alertEnabled;
    try {
      const { error } = await supabase
        .from('subscriptions')
        .update({ alert_enabled: newValue })
        .eq('user_id', user.id)
        .eq('calendar_id', calendar.id);
      if (error) throw error;
      setAlertEnabled(newValue);
      toast.success(newValue ? 'Email alerts enabled' : 'Email alerts disabled');
    } catch {
      toast.error('Failed to update alert preference');
    } finally {
      setAlertLoading(false);
    }
  };

  // Get subscription/ticket status
  const { isActive, expiresAt, status } = useSubscriptionStatus(calendar.id, user?.id);
  const { hasPurchased } = useTicketStatus(calendar.id, events[0]?.id, user?.id);

  const handleFreeSubscribe = async () => {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    if (isSubscribed) {
      toast.error('You are already subscribed to this calendar');
      return;
    }

    // Check subscription limit
    if (calendar.subscription_limit && subscriberCount >= calendar.subscription_limit) {
      toast.error('This calendar has reached its subscription limit');
      return;
    }

    try {
      setSubscribing(true);
      
      await createFreeSubscription(calendar.id, user.id);

      // Only open Google Calendar if subscription was successful
      window.open(getGoogleCalendarSubscribeUrl(calendar.google_calendar_url), '_blank');
      onSubscriptionChange();
      toast.success('Successfully subscribed to calendar');
    } catch (err) {
      console.error('Error subscribing:', err);
      
      if (err instanceof Error) {
        if (err.message.includes('duplicate key')) {
          toast.error('You are already subscribed to this calendar');
        } else if (err.message.includes('not found')) {
          toast.error('Calendar not found. Please refresh the page and try again');
        } else if (err.message.includes('network')) {
          toast.error('Network error. Please check your connection and try again');
        } else {
          toast.error(`Subscription failed: ${err.message}`);
        }
      } else {
        toast.error('Failed to subscribe to calendar. Please try again');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!user) {
      toast.error('Please log in to unsubscribe');
      return;
    }

    try {
      const { error } = await supabase
        .from('subscriptions')
        .delete()
        .eq('user_id', user.id)
        .eq('calendar_id', calendar.id);

      if (error) throw error;
      onSubscriptionChange();
      toast.success('Successfully unsubscribed from calendar');
    } catch (err) {
      console.error('Error unsubscribing:', err);
      toast.error('Failed to unsubscribe from calendar');
    }
  };

  // Check if subscriptions are full
  const isSubscriptionsFull = calendar.subscription_limit && subscriberCount >= calendar.subscription_limit;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Calendar Links</h3>
        
        <div className="mb-4">
          <NextEventCountdown events={events} />
        </div>

        {user ? (
          <>
            {/* Subscription Status */}
            {isSubscribed && (
              <SubscriptionStatus isActive={isActive} expiresAt={expiresAt} />
            )}

            {/* Subscribe/Purchase Button */}
            {calendar.payment_type === 'free' ? (
              <>
                <button
                  onClick={isSubscribed ? handleUnsubscribe : handleFreeSubscribe}
                  disabled={subscribing || (!isSubscribed && isSubscriptionsFull)}
                  className={`w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
                    isSubscribed
                      ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      : isSubscriptionsFull
                      ? 'text-gray-500 bg-gray-100 cursor-not-allowed'
                      : 'text-white bg-purple-600 hover:bg-purple-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 mb-4`}
                >
                  <Users className="h-4 w-4 mr-2" />
                  {subscribing ? 'Processing...' : isSubscribed ? 'Unsubscribe' : isSubscriptionsFull ? 'Subscriptions Full' : 'Subscribe'}
                </button>
                {!isSubscribed && isSubscriptionsFull && (
                  <p className="text-sm text-red-600 mt-2">
                    This calendar has reached its maximum of {calendar.subscription_limit} subscribers
                  </p>
                )}
              </>
            ) : calendar.payment_type === 'subscription' ? (
              <SubscribeButton
                calendarId={calendar.id}
                isSubscribed={isSubscribed}
                onSubscriptionChange={onSubscriptionChange}
                price={calendar.subscription_price_cents || 0}
                status={status}
                expiresAt={expiresAt}
                isSubscriptionsFull={isSubscriptionsFull}
                subscriptionLimit={calendar.subscription_limit}
              />
            ) : calendar.payment_type === 'one_time' && events[0] ? (
              <PurchaseTicketButton
                calendarId={calendar.id}
                eventId={events[0].id}
                price={calendar.event_price_cents || 0}
                hasPurchased={hasPurchased}
              />
            ) : null}
          </>
        ) : (
          <SubscriptionPrompt 
            calendarName={calendar.name} 
            calendarId={calendar.id}
            paymentType={calendar.payment_type as 'free' | 'subscription' | 'one_time'}
            price={calendar.payment_type === 'subscription' ? calendar.subscription_price_cents : calendar.event_price_cents}
            isSubscriptionsFull={isSubscriptionsFull}
            subscriptionLimit={calendar.subscription_limit}
          />
        )}

        {isSubscribed && (
          <div className="mt-3 mb-4">
            <button
              onClick={handleToggleAlert}
              disabled={alertLoading}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2 text-sm text-gray-700">
                <Bell className={`h-4 w-4 ${alertEnabled ? 'text-purple-600' : 'text-gray-400'}`} />
                <span>Email alerts for updates</span>
              </div>
              <div
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  alertEnabled ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                    alertEnabled ? 'translate-x-[18px]' : 'translate-x-[2px]'
                  }`}
                />
              </div>
            </button>
          </div>
        )}

        {/* Subscription Benefits */}
        {!isSubscribed && (
          <div className="mt-4 bg-purple-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-purple-900 mb-2">
              Subscribe to get:
            </h4>
            <ul className="space-y-2 text-sm text-purple-800">
              <li className="flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                Events synced to your calendar
              </li>
              <li className="flex items-center">
                <Bell className="h-4 w-4 mr-2 text-purple-600" />
                Updates when events change
              </li>
              {calendar.physical_address && calendar.address_visibility === 'subscribers' && (
                <li className="flex items-center">
                  <Users className="h-4 w-4 mr-2 text-purple-600" />
                  Access to event locations
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Streaming Links - Always visible */}
        <div className="mt-6 space-y-3">
          {calendar.streaming_urls?.Twitch && (
            <a
              href={calendar.streaming_urls.Twitch}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center w-full px-4 py-2 text-gray-700 hover:text-purple-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Twitch className="h-5 w-5 mr-3" />
              Watch on Twitch
            </a>
          )}
          
          {calendar.streaming_urls?.YouTube && (
            <a
              href={calendar.streaming_urls.YouTube}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center w-full px-4 py-2 text-gray-700 hover:text-purple-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Youtube className="h-5 w-5 mr-3" />
              Watch on YouTube
            </a>
          )}

          {calendar.custom_url && (
            <a
              href={calendar.custom_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center w-full px-4 py-2 text-gray-700 hover:text-purple-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Globe2 className="h-5 w-5 mr-3" />
              Visit Website
            </a>
          )}
        </div>
      </div>

      {calendar.is_public && (
        <PastEventsSection
          events={events}
          limit={10}
          hideDescription={calendar.payment_type === 'subscription' || calendar.payment_type === 'one_time'}
        />
      )}

      {user && isSubscribed && (
        <EventFeedbackSection
          events={events}
          calendarId={calendar.id}
        />
      )}
    </div>
  );
}